import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { InputDollar } from "@/components/_pages/ModelPage/Sidebar/InputDollar";
import { InputPercent } from "@/components/_pages/ModelPage/Sidebar/InputPercent";
import {
  isLineOfCreditIncludedAtom,
  lenderLineOfCreditDrawdownAtom,
  lenderLineOfCreditDrawdownYearAtom,
  lenderLineOfCreditTotalInputAmountAtom,
  lineOfCreditErrorMessageAtom,
  lineOfCreditInterestRateAtom,
  lineOfCreditIsInErrorAtom,
} from "@/financeModels/owasco/general";
import { iterationCountAtom } from "@/financeModels/owasco/iterations";
import { numberToPercentInput, percentInputToNumber } from "@/utils/format";
import { toNumberOrNull } from "@/utils/math";
import { Alert, Stack } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { ChangeEvent } from "react";
import { TbCashBanknote } from "react-icons/tb";
import { InputSelect } from "../../Sidebar/InputSelect";
import { InputToggle } from "../../Sidebar/InputToggle";

const Panel = () => {
  const [interestRate, setInterestRate] = useAtom(lineOfCreditInterestRateAtom);
  const [total, setTotal] = useAtom(lenderLineOfCreditTotalInputAmountAtom);
  const [year, setYear] = useAtom(lenderLineOfCreditDrawdownYearAtom);
  const [drawdown, setDrawdown] = useAtom(lenderLineOfCreditDrawdownAtom);
  const [isIncluded, setIsIncluded] = useAtom(isLineOfCreditIncludedAtom);
  const iterationCount = useAtomValue(iterationCountAtom);
  const yearSelectData = Array.from(
    { length: iterationCount || 1 },
    (_, i) => ({
      value: `${i + 1}`,
      label: `Year ${i + 1}`,
    }),
  );

  const errorMessage = useAtomValue(lineOfCreditErrorMessageAtom);

  return (
    <Stack gap={4}>
      <InputToggle
        label={
          <LabelExplainer
            label="Line of Credit Included?"
            explanation={
              <Doc path="@/docs/pages/model/sidebar/lineOfCreditIncluded.mdx" />
            }
          />
        }
        checked={isIncluded}
        onChange={(event) => {
          setIsIncluded(
            (event as ChangeEvent<HTMLInputElement>).currentTarget.checked,
          );
        }}
      />
      {isIncluded && (
        <>
          <InputPercent
            label={
              <LabelExplainer
                label="Interest Rate"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/lineOfCreditInterestRate.mdx" />
                }
              />
            }
            value={numberToPercentInput(interestRate)}
            onChange={(value) => setInterestRate(percentInputToNumber(value))}
          />
          <InputDollar
            label={
              <LabelExplainer
                label="Total Amount"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/lineOfCreditTotalAmount.mdx" />
                }
              />
            }
            value={toNumberOrNull(total) || ""}
            onChange={(value) => setTotal(toNumberOrNull(value))}
          />
          <InputDollar
            label={
              <LabelExplainer
                label="Drawdown Amount"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/lineOfCreditDrawdown.mdx" />
                }
              />
            }
            value={toNumberOrNull(drawdown) || ""}
            onChange={(value) => setDrawdown(toNumberOrNull(value))}
          />
          <InputSelect
            labelWidth={201}
            data={yearSelectData}
            label={
              <LabelExplainer
                label="Drawdown Year"
                explanation={
                  <Doc path="@/docs/pages/model/sidebar/lineOfCreditDrawdownYear.mdx" />
                }
              />
            }
            value={`${toNumberOrNull(year) || ""}`}
            onChange={(value) => setYear(toNumberOrNull(Number(value)))}
          />
        </>
      )}

      {errorMessage && (
        <Alert variant="light" color="red" ta={"center"}>
          {errorMessage}
        </Alert>
      )}
    </Stack>
  );
};

export const ModuleLenderLineOfCredit = {
  key: "Lender Line Of Credit",
  icon: <TbCashBanknote />,
  control: "Lender Line Of Credit",
  panel: <Panel />,
  errorAtom: lineOfCreditIsInErrorAtom,
};
